import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { bemCssModules, getTestId } from '@utils';
import { useTapped, useNavigate } from '@utils/hooks';

import { cardsActions, useStore } from '@modules';
import { GETCardsResponse } from '@modules/cards/types';

import { useCountDownNotificationContext } from '@containers/App/routes/private/CountDownNotificationContext';
import LoaderLayout from '@layouts/LoaderLayout';

import Card from './Card';
import AddCardIcon from './add_card.svg';

import style from './Cards.scss';

const cn = bemCssModules(style, 'Cards');
const ti = getTestId('Cards');

const MAX_SHOW_CARDS_LIMIT = 3;
const LAST_FOUR_NUMBERS = 4;

const Cards: CustomFC = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isShowAll, setShowAll] = useState(false);
  const [cards, setCards] = useState<GETCardsResponse[]>([]);
  const [deletedCardsList, setDeletedCardsList] = useState<string[]>([]);

  const { handlers, isTapped } = useTapped();

  const [{ id }, setContext] = useCountDownNotificationContext();

  const [store, dispatch] = useStore();
  const { list, loaders } = store.cards;

  useEffect(() => {
    setCards(list);

    return () => setDeletedCardsList([]);
  }, [list.length]);

  const handleShowAll = () => setShowAll(prev => !prev);
  const handleAddCard = () => navigate(CONST.nav.profileCardsAdd);
  const handleCancel = (cardId: string) => {
    setContext('id', undefined);
    setDeletedCardsList(prev => prev.filter(card => card !== cardId));
    setCards(list);
  };

  const handleTimeExpired = (cardId: string) => {
    setContext('id', undefined);
    setDeletedCardsList(prev => [...prev, cardId]);

    dispatch(cardsActions.deleteCard({ id: cardId }));
  };

  const handleDeleteCard = (cardId: string) => {
    if (id) handleTimeExpired(id);
    const cardNumber = cards.find(card => card.id === cardId)?.card_number || '';
    const formattedCardNumber = cardNumber.substring(cardNumber.length - LAST_FOUR_NUMBERS);

    setContext('id', cardId);
    setContext('text', t('notifications.card_is_removed', { cardNumber: formattedCardNumber }));
    setContext('onTimeExpired', () => handleTimeExpired(cardId));
    setContext('onCancel', () => handleCancel(cardId));

    setDeletedCardsList(prev => [...prev, cardId]);
  };

  const cardsList = cards.slice(0, isShowAll ? cards.length : MAX_SHOW_CARDS_LIMIT);
  const showAllCards = cards.length > MAX_SHOW_CARDS_LIMIT;
  const isLoading = !list.length && loaders.getCards !== false;

  return (
    <div className={cn(null, [className])} data-testid={ti()}>
      <LoaderLayout isLoading={isLoading}>
        <h2 className={cn('title')}>{t('card.cards')}</h2>
        <div className={cn('cards')}>
          {cardsList
            .filter(card => !deletedCardsList.includes(card.id))
            .map(({ id: cardId, payment_system, card_number }) => (
              <Card
                key={cardId}
                id={cardId}
                paymentSystem={payment_system}
                cardNumber={card_number}
                onDelete={handleDeleteCard}
                testId={ti('card')}
              />
            ))}
          <button
            className={cn('card', { isTapped })}
            type="button"
            onClick={handleAddCard}
            data-testid={ti('new-card')}
            {...handlers}
          >
            <AddCardIcon className={cn('icon')} />
            <span>{t('card.new_card')}</span>
          </button>
        </div>

        {showAllCards && (
          <button className={cn('button')} type="button" onClick={handleShowAll}>
            {isShowAll ? t('wallets.show_less') : t('cards.all_cards')}
          </button>
        )}
      </LoaderLayout>
    </div>
  );
};

export default Cards;
